import * as THREE from "three";

const BODY_MAT = new THREE.MeshStandardMaterial({
  color: 0x5f6c62,
  roughness: 0.72,
  metalness: 0.18,
  emissive: new THREE.Color(0x0f1412),
  emissiveIntensity: 0.16,
});

const DARK_MAT = new THREE.MeshStandardMaterial({
  color: 0x2a2f31,
  roughness: 0.68,
  metalness: 0.22,
});

const GLASS_MAT = new THREE.MeshStandardMaterial({
  color: 0x3f555d,
  roughness: 0.2,
  metalness: 0.35,
  emissive: new THREE.Color(0x08141a),
  emissiveIntensity: 0.28,
});

export class C130 {
  readonly group = new THREE.Group();
  readonly gunnerPod = new THREE.Group();

  static readonly LAYER = 1;

  constructor() {
    this.group.add(this.buildBody());
    this.gunnerPod.position.set(-3.8, -1.55, 0);
    this.group.add(this.gunnerPod);
    this.group.traverse((obj) => obj.layers.set(C130.LAYER));
    this.gunnerPod.layers.enableAll();
  }

  applyTransform(position: { x: number; y: number; z: number }, heading: number, bank: number): void {
    this.group.position.set(position.x, position.y, position.z);
    this.group.rotation.set(0, 0, 0);
    this.group.rotateY(heading);
    this.group.rotateZ(bank);
  }

  private buildBody(): THREE.Group {
    const root = new THREE.Group();
    root.userData["isAircraft"] = true;

    root.add(this.createFuselage());
    root.add(this.createWingAssembly());
    root.add(this.createTailAssembly());
    root.add(this.createEngineAssembly());
    root.add(this.createSponsons());
    root.add(this.createSensorFairings());

    return root;
  }

  private createFuselage(): THREE.Group {
    const fuselage = new THREE.Group();

    const centerBody = this.makeMesh(new THREE.BoxGeometry(18, 4.2, 4.6), BODY_MAT);
    centerBody.position.x = -0.5;
    fuselage.add(centerBody);

    const nose = this.makeMesh(new THREE.CapsuleGeometry(2.2, 3.8, 5, 12), BODY_MAT);
    nose.rotation.z = Math.PI / 2;
    nose.position.set(10.7, 0.15, 0);
    fuselage.add(nose);

    const cockpit = this.makeMesh(new THREE.BoxGeometry(2.6, 1.05, 3.15), GLASS_MAT);
    cockpit.position.set(12.2, 1.2, 0);
    fuselage.add(cockpit);

    const chin = this.makeMesh(new THREE.BoxGeometry(1.9, 0.9, 2.2), DARK_MAT);
    chin.position.set(9.5, -1.75, 0);
    fuselage.add(chin);

    const tailBoom = this.makeMesh(new THREE.BoxGeometry(6.8, 3.1, 3.4), BODY_MAT);
    tailBoom.position.set(-12.4, 0.45, 0);
    fuselage.add(tailBoom);

    const rearTaper = this.makeMesh(new THREE.CylinderGeometry(1.6, 2.1, 4.2, 10), BODY_MAT);
    rearTaper.rotation.z = Math.PI / 2;
    rearTaper.position.set(-18.1, 0.55, 0);
    fuselage.add(rearTaper);

    const ramp = this.makeMesh(new THREE.BoxGeometry(2.6, 2.15, 3.2), DARK_MAT);
    ramp.position.set(-20.2, -0.15, 0);
    ramp.rotation.z = -0.12;
    fuselage.add(ramp);

    return fuselage;
  }

  private createWingAssembly(): THREE.Group {
    const wings = new THREE.Group();

    const wing = this.makeMesh(new THREE.BoxGeometry(5.4, 0.55, 32), BODY_MAT);
    wing.position.set(1.2, 1.45, 0);
    wings.add(wing);

    const wingBox = this.makeMesh(new THREE.BoxGeometry(3.2, 0.9, 12), DARK_MAT);
    wingBox.position.set(-0.8, 1.25, 0);
    wings.add(wingBox);

    for (const z of [-13.5, 13.5]) {
      const tip = this.makeMesh(new THREE.BoxGeometry(1.1, 0.18, 2.8), BODY_MAT);
      tip.position.set(1.4, 1.58, z);
      tip.rotation.x = z > 0 ? -0.12 : 0.12;
      wings.add(tip);
    }

    return wings;
  }

  private createTailAssembly(): THREE.Group {
    const tail = new THREE.Group();

    const hStab = this.makeMesh(new THREE.BoxGeometry(3, 0.35, 12), BODY_MAT);
    hStab.position.set(-17.2, 3.1, 0);
    tail.add(hStab);

    const vStab = this.makeMesh(new THREE.BoxGeometry(3.2, 6.4, 0.55), BODY_MAT);
    vStab.position.set(-17.9, 4.2, 0);
    tail.add(vStab);

    const finCap = this.makeMesh(new THREE.BoxGeometry(1.4, 0.35, 2.1), BODY_MAT);
    finCap.position.set(-17.2, 7.3, 0);
    tail.add(finCap);

    return tail;
  }

  private createEngineAssembly(): THREE.Group {
    const engines = new THREE.Group();
    const engineX = [-5.6, -1.2, 3.2, 7.6];

    for (const [idx, x] of engineX.entries()) {
      for (const side of [-1, 1] as const) {
        const z = side * 7.6;

        const nacelle = this.makeMesh(new THREE.CylinderGeometry(0.95, 1.08, 4.2, 12), BODY_MAT);
        nacelle.rotation.z = Math.PI / 2;
        nacelle.position.set(x, 0.95, z);
        engines.add(nacelle);

        const intake = this.makeMesh(new THREE.CylinderGeometry(0.82, 0.82, 0.35, 12), DARK_MAT);
        intake.rotation.z = Math.PI / 2;
        intake.position.set(x + 2.2, 0.95, z);
        engines.add(intake);

        const propMat = GLASS_MAT.clone();
        propMat.transparent = true;
        propMat.opacity = 0.22 + idx * 0.01;
        const propDisc = this.makeMesh(new THREE.CylinderGeometry(2.45, 2.45, 0.08, 20), propMat);
        propDisc.rotation.z = Math.PI / 2;
        propDisc.position.set(x + 2.8, 0.95, z);
        engines.add(propDisc);

        const spinner = this.makeMesh(new THREE.ConeGeometry(0.34, 0.9, 8), DARK_MAT);
        spinner.rotation.z = -Math.PI / 2;
        spinner.position.set(x + 3.05, 0.95, z);
        engines.add(spinner);

        const pylon = this.makeMesh(new THREE.BoxGeometry(0.7, 1.2, 1.3), BODY_MAT);
        pylon.position.set(x, 1.55, z);
        engines.add(pylon);
      }
    }

    return engines;
  }

  private createSponsons(): THREE.Group {
    const sponsons = new THREE.Group();

    for (const side of [-1, 1] as const) {
      const fairing = this.makeMesh(new THREE.BoxGeometry(5.8, 1.25, 1.25), BODY_MAT);
      fairing.position.set(-7.2, -1.55, side * 2.9);
      sponsons.add(fairing);

      const pod = this.makeMesh(new THREE.CylinderGeometry(0.38, 0.38, 2.2, 8), DARK_MAT);
      pod.rotation.x = Math.PI / 2;
      pod.position.set(-9.4, -1.8, side * 2.95);
      sponsons.add(pod);
    }

    return sponsons;
  }

  private createSensorFairings(): THREE.Group {
    const fairings = new THREE.Group();

    const dorsal = this.makeMesh(new THREE.BoxGeometry(2.8, 0.55, 1.5), DARK_MAT);
    dorsal.position.set(3.2, 2.45, 0);
    fairings.add(dorsal);

    const sideBlister = this.makeMesh(new THREE.CylinderGeometry(0.42, 0.62, 1.5, 8), DARK_MAT);
    sideBlister.rotation.z = Math.PI / 2;
    sideBlister.position.set(-4.1, -1.55, 0);
    fairings.add(sideBlister);

    return fairings;
  }

  private makeMesh(geometry: THREE.BufferGeometry, material: THREE.Material): THREE.Mesh {
    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData["isAircraft"] = true;
    return mesh;
  }
}
